import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar, Pie } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const SolutionDilutionCalculator = () => {
  const [dilutionData, setDilutionData] = useState({
    initialConcentration: '',
    initialVolume: '',
    finalVolume: '',
    units: 'L'
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDilutionData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateDilution = () => {
    const { initialConcentration, initialVolume, finalVolume, units } = dilutionData;
    
    const initConc = parseFloat(initialConcentration) || 0;
    const initVol = parseFloat(initialVolume) || 0;
    const finVol = parseFloat(finalVolume) || 0;

    if (initConc === 0 || isNaN(initConc)) {
      setError('Please enter a valid initial concentration');
      return;
    }

    if (initVol === 0 || isNaN(initVol)) {
      setError('Please enter a valid initial volume');
      return;
    }

    if (finVol === 0 || isNaN(finVol)) {
      setError('Please enter a valid final volume');
      return;
    }

    // Convert volumes to liters if needed
    let initialVolumeInLiters = initVol;
    let finalVolumeInLiters = finVol;
    if (units === 'mL') {
      initialVolumeInLiters = initVol / 1000;
      finalVolumeInLiters = finVol / 1000;
    }

    // Calculate final concentration using dilution formula: C1V1 = C2V2
    const finalConcentration = (initConc * initialVolumeInLiters) / finalVolumeInLiters;

    const calculationSteps = [
      'Step 1: Prepare the values',
      `Initial concentration: ${initConc} M`,
      `Initial volume: ${initVol} ${units}`,
      `Final volume: ${finVol} ${units}`,
      `Initial volume in liters: ${initialVolumeInLiters} L`,
      `Final volume in liters: ${finalVolumeInLiters} L`,
      '\nStep 2: Apply the dilution formula',
      `C1V1 = C2V2`,
      `(${initConc} M) * (${initialVolumeInLiters} L) = (C2) * (${finalVolumeInLiters} L)`,
      `C2 = (C1 * V1) / V2`,
      `Final concentration = (${initConc} * ${initialVolumeInLiters}) / ${finalVolumeInLiters} = ${finalConcentration.toFixed(3)} M`
    ];

    const newResults = {
      finalConcentration: finalConcentration.toFixed(3),
      calculationSteps
    };

    setResults(newResults);

    // Update chart data
    setChartData({
      bar: {
        labels: ['Final Concentration'],
        datasets: [{
          label: 'Values',
          data: [finalConcentration],
          backgroundColor: [
            'rgba(75, 192, 192, 0.5)'
          ],
          borderColor: [
            'rgba(75, 192, 192, 1)'
          ],
          borderWidth: 1
        }]
      }
    });
  };

  return (
    <>
     <Helmet>
      <title>Dilution Calculator - Solution Concentration Analysis</title>
      <meta name="description" content="Calculate solution dilution using initial concentration, initial volume, and final volume. Understand dilution concepts with our interactive dilution calculator." />
      <meta name="keywords" content="dilution calculator, solution concentration, chemistry calculator, solution preparation, chemistry education, laboratory work" />
      <meta name="author" content="Your Company Name" />
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta property="og:title" content="Dilution Calculator - Solution Concentration Analysis" />
      <meta property="og:description" content="Calculate solution dilution using initial concentration, initial volume, and final volume. Understand dilution concepts with our interactive dilution calculator." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://yourdomain.com/dilution-calculator" />
      <meta property="og:image" content="https://yourdomain.com/dilution-calculator-preview.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:site_name" content="Chemistry Calculators" />
      <meta property="og:locale" content="en_US" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@your_twitter_handle" />
      <meta name="twitter:creator" content="@your_twitter_handle" />
      <meta name="twitter:title" content="Dilution Calculator - Solution Concentration Analysis" />
      <meta name="twitter:description" content="Calculate solution dilution using initial concentration, initial volume, and final volume. Understand dilution concepts with our interactive dilution calculator." />
      <meta name="twitter:image" content="https://yourdomain.com/dilution-calculator-preview.jpg" />
      <meta name="twitter:image:alt" content="Dilution Calculator Preview" />
      <meta name="pinterest" content="nopin" />
      <meta name="pinterest-rich-pin" content="true" />
      <meta itemprop="name" content="Dilution Calculator - Solution Concentration Analysis" />
      <meta itemprop="description" content="Calculate solution dilution using initial concentration, initial volume, and final volume. Understand dilution concepts with our interactive dilution calculator." />
      <meta itemprop="image" content="https://yourdomain.com/dilution-calculator-preview.jpg" />
      <meta name="theme-color" content="#ffffff" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Dilution Calculator" />
    </Helmet>

    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🔬</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Dilution Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Solution Concentration Analysis</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          Calculate the dilution of a solution using the initial concentration, initial volume, and final volume. 
          This calculator helps you understand dilution concepts and solution preparation.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="initialConcentration">
            Initial Concentration (M):
          </label>
          <input
            type="number"
            id="initialConcentration"
            name="initialConcentration"
            value={dilutionData.initialConcentration}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter initial concentration in Molarity"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="initialVolume">
            Initial Volume:
          </label>
          <div className="flex">
            <input
              type="number"
              id="initialVolume"
              name="initialVolume"
              value={dilutionData.initialVolume}
              onChange={handleInputChange}
              className="appearance-none flex-1 bg-gray-200 text-gray-700 border rounded-l py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter initial volume"
            />
            <select
              id="units"
              name="units"
              value={dilutionData.units}
              onChange={handleInputChange}
              className="appearance-none w-24 bg-gray-200 text-gray-700 border rounded-r py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            >
              <option value="L">L</option>
              <option value="mL">mL</option>
            </select>
          </div>
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="finalVolume">
            Final Volume:
          </label>
          <input
            type="number"
            id="finalVolume"
            name="finalVolume"
            value={dilutionData.finalVolume}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter final volume"
          />
        </div>

        <button
          onClick={calculateDilution}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Calculate Dilution
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
            
            <div className="grid grid-cols-1  gap-8 mb-12">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-xl font-semibold mb-4">Dilution Properties</h4>
                <div className="space-y-2">
                  <p className="flex justify-between">
                    <span>Final Concentration (M)</span>
                    <span className="font-medium">{results.finalConcentration}</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-12 mt-12">
              <div>
                <h4 className="text-xl font-semibold mb-4">Bar Chart</h4>
                <div className="h-96">
                  <Bar
                    data={chartData.bar}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Value'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Solution Property'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Solution Properties'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mt-12 bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-4">Calculation Steps</h4>
              <div className="space-y-4">
                {results.calculationSteps.map((step, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                    <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Dilution Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the Dilution Calculator effectively:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Gather Solution Data</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Measure the initial concentration accurately</li>
                <li>Measure the initial volume</li>
                <li>Determine the desired final volume</li>
                <li>Ensure all measurements are precise</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Solution Information</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Input the initial concentration in Molarity</li>
                <li>Enter the initial volume</li>
                <li>Specify the desired final volume</li>
                <li>Select the appropriate volume unit (L or mL)</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Analyze Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Review the calculated final concentration</li>
                <li>Use the bar chart for visual analysis</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-6">
            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">What is dilution?</h3>
              <p className="text-gray-600">
                Dilution is the process of reducing the concentration of a solute in a solution, usually by adding more solvent. 
                It's calculated using the formula: C1V1 = C2V2.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">Why is dilution important in chemistry?</h3>
              <p className="text-gray-600">
                Dilution is crucial in chemistry because it:
                <ul className="list-disc pl-6 mt-2">
                  <li>Helps in preparing solutions of desired concentration</li>
                  <li>Ensures accurate solution preparation for experiments</li>
                  <li>Is used in chemical reactions and titrations</li>
                  <li>Helps maintain consistent concentrations in experiments</li>
                </ul>
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">How do I convert between mL and L in the calculator?</h3>
              <p className="text-gray-600">
                The calculator automatically handles unit conversion. Just select your preferred unit (mL or L) from the dropdown menu next to the volume input.
                If you enter volume in mL, the calculator will convert it to liters for the calculation.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">What if I don't know the initial concentration of my solution?</h3>
              <p className="text-gray-600">
                You can find the initial concentration of a solution by:
                <ol className="list-decimal pl-6 mt-2">
                  <li>Using a concentration meter</li>
                  <li>Performing a titration</li>
                  <li>Looking it up in a chemistry reference book</li>
                </ol>
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">Why are my results different from expected?</h3>
              <p className="text-gray-600">
                If your results seem unexpected, check:
                <ul className="list-disc pl-6 mt-2">
                  <li>If you've entered the correct initial concentration</li>
                  <li>If your volume measurements are accurate</li>
                  <li>If all values are in the correct units</li>
                </ul>
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">Can I use this calculator for serial dilutions?</h3>
              <p className="text-gray-600">
                While this calculator is primarily for single dilutions, you can use it for serial dilutions by:
                <ol className="list-decimal pl-6 mt-2">
                <li>Calculating the initial dilution</li>
                  <li>Using the resulting solution for the next dilution step</li>
                </ol>
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">Is dilution affected by temperature?</h3>
              <p className="text-gray-600">
                Yes, dilution can be temperature-dependent because:
                <ul className="list-disc pl-6 mt-2">
                  <li>Water density changes with temperature</li>
                  <li>Volume measurements can be affected</li>
                  <li>It's important to specify temperature when reporting concentration values</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-lg font-semibold mb-2">Visual Analysis</h3>
              <p className="text-gray-600">Interactive bar charts for easy visualization of solution properties</p>
            </div>
            <div className="text-center p-6 bg-green-50 rounded-lg">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-lg font-semibold mb-2">Precise Calculations</h3>
              <p className="text-gray-600">Accurate dilution calculations with step-by-step breakdown</p>
            </div>
            <div className="text-center p-6 bg-yellow-50 rounded-lg">
              <div className="text-4xl mb-4">🔄</div>
              <h3 className="text-lg font-semibold mb-2">Unit Conversion</h3>
              <p className="text-gray-600">Automatic conversion between liters and milliliters</p>
            </div>
            <div className="text-center p-6 bg-purple-50 rounded-lg">
              <div className="text-4xl mb-4">📚</div>
              <h3 className="text-lg font-semibold mb-2">Educational Value</h3>
              <p className="text-gray-600">Detailed explanations of chemistry concepts</p>
            </div>
            <div className="text-center p-6 bg-pink-50 rounded-lg">
              <div className="text-4xl mb-4">⚙️</div>
              <h3 className="text-lg font-semibold mb-2">User-Friendly</h3>
              <p className="text-gray-600">Intuitive interface with clear input fields</p>
            </div>
            <div className="text-center p-6 bg-orange-50 rounded-lg">
              <div className="text-4xl mb-4">🔄</div>
              <h3 className="text-lg font-semibold mb-2">Real-time Updates</h3>
              <p className="text-gray-600">Instant calculation results as you type</p>
            </div>
          </div>

          <div className="mt-12">
            <h3 className="text-xl font-semibold mb-4">Key Benefits</h3>
            <ul className="list-disc pl-8 space-y-2">
              <li>Save time with automatic calculations</li>
              <li>Reduce calculation errors with built-in validation</li>
              <li>Learn chemistry concepts through practical examples</li>
              <li>Accessible from any device</li>
              <li>Free to use for educational and professional purposes</li>
              <li>Perfect for students, teachers, and professionals</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding Dilution <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Dilution is a process used to reduce the concentration of a solute in a solution. It is important in:
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Chemical Reactions:</span> Ensuring reactant amounts and product yields are accurate
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Solution Preparation:</span> Making accurate dilutions and standard solutions
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Laboratory Work:</span> Ensuring consistent solution concentrations
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Industrial Processes:</span> Controlling chemical manufacturing processes
            </li>
          </ul>
          <p className="italic text-gray-500 mt-6">
            Note: Dilution may be temperature-dependent as it involves volume measurements. Always specify the temperature when reporting dilution values.
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default SolutionDilutionCalculator;